// src/public-routes/sectors/FinancialSector.jsx
import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaChartLine,
    FaShieldAlt,
    FaBalanceScale,
    FaFileContract,
    FaSearch,
    FaUsers,
    FaHandshake,
    FaTrophy
} from 'react-icons/fa';

const FinancialSector = () => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const stats = [
        { number: '78%', label: 'of financial consumers research online before deciding' },
        { number: '3.2x', label: 'higher trust with compliant, transparent marketing' },
        { number: '89%', label: 'of financial brands say digital is critical for growth' },
        { number: '56%', label: 'increase in lead quality with targeted financial campaigns' }
    ];

    const approaches = [
        {
            title: "Compliant Performance Marketing",
            description: "Navigate regulatory requirements while driving growth with compliant, performance-driven campaigns that maintain credibility and security.",
            icon: FaShieldAlt
        },
        {
            title: "Trust & Authority Building",
            description: "Establish your brand as a trusted financial authority through thought leadership, educational content, and reputation management strategies.",
            icon: FaBalanceScale
        },
        {
            title: "Regulated Lead Generation",
            description: "Generate qualified leads within compliance frameworks using targeted campaigns, optimized landing pages, and intelligent qualification processes.",
            icon: FaFileContract
        },
        {
            title: "Client Retention & Growth",
            description: "Build lasting client relationships through personalized communication, value-added content, and strategic cross-selling campaigns.",
            icon: FaHandshake
        }
    ];

    const benefits = [
        ['Compliant Growth', 'Scale your marketing efforts while maintaining full regulatory compliance and brand integrity'],
        ['Enhanced Trust & Credibility', 'Build lasting trust with transparent, educational marketing that positions your brand as an authority'],
        ['Qualified Lead Generation', 'Attract high-intent prospects actively seeking financial solutions with targeted campaigns'],
        ['Client Lifetime Value', 'Maximize client relationships through strategic retention and cross-selling initiatives'],
        ['Risk-Managed Marketing', 'Navigate complex regulatory environments with marketing strategies designed for financial services']
    ];

    const questions = [
        'How can you grow while maintaining regulatory compliance?',
        'What strategies will build trust in a security-sensitive market?',
        'How do you generate qualified leads within compliance frameworks?',
        'Which channels deliver compliant, high-quality financial leads?',
        'How can you differentiate in an increasingly competitive financial landscape?'
    ];

    return (
        <div className="financial-sector-page st-page">
            {/* Top Image Section */}
            <div className="st-decorative-image-container">
                <img
                    src={require('./Financial.PNG')}
                    alt="Financial Services Digital Marketing - Compliant Growth Strategies"
                    loading="eager"
                />
            </div>

            {/* Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Financial & Professional</span> Services
                    </h1>
                    <p>Precision and trust in regulated industries with compliant, performance-driven strategies that grow your business</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Operating in <span className="sv-keyword-highlight">regulated industries</span> requires precision, trust, and deep compliance awareness. We deliver compliant, performance-driven strategies for financial services, banking, and professional firms.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>financial services marketing</strong> combines strategic thinking, regulatory understanding, and advanced tools to create campaigns that drive growth while maintaining the highest standards of credibility and security. We help you grow while protecting your reputation.
                                </p>
                                <p className="sv-description">
                                    We understand the unique challenges financial and professional services face: strict regulatory requirements, the need for absolute trust, complex products, long decision cycles, and the critical importance of compliance in every marketing activity.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Financial Strategy
                                    </Button>
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Financial Services Marketing</span> Matters
                        </h2>
                        <Row className="sv-stats-grid">
                            {stats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What We Do Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Do for Financial Services</h2>
                                <p className="sv-section-description">
                                    <strong>Financial services marketing</strong> requires a delicate balance between driving growth and maintaining compliance. It's about building trust through transparent communication, establishing authority through thought leadership, and generating qualified leads within regulatory frameworks.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions We Answer:</h4>
                                    {questions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Overview */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in Financial Services Marketing?</h2>
                                <p className="sv-section-description">
                                    The financial services landscape is increasingly digital. Today's consumers expect seamless, trustworthy digital experiences from their financial providers. A comprehensive, compliant marketing strategy is essential to meet these expectations while driving measurable growth.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Regulatory Compliance</h5>
                                        <p>Grow confidently with marketing strategies designed for regulated environments</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-star"></i>
                                        <h5>Trust Building</h5>
                                        <p>Establish credibility and authority in security-sensitive markets</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-users"></i>
                                        <h5>Quality Clients</h5>
                                        <p>Attract high-value clients actively seeking professional financial services</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Sustainable Growth</h5>
                                        <p>Build long-term client relationships that drive predictable revenue</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Financial Services Marketing</h2>
                                <p className="sv-section-description">
                                    Reachtix offers a comprehensive suite of financial services marketing solutions designed to drive growth, build trust, and maintain compliance in regulated industries.
                                </p>

                                <Row className="sv-services-grid">
                                    {approaches.map((service, index) => (
                                        <Col lg={6} md={6} key={index} className="mb-4">
                                            <div className="sv-approach-card p-4 h-100">
                                                <div className="sv-approach-icon">
                                                    <service.icon />
                                                </div>
                                                <div className="sv-approach-content">
                                                    <h4>{service.title}</h4>
                                                    <p className="service-description">{service.description}</p>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Table Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Key Benefits of Financial Marketing With Reachtix</h2>
                                <div className="table-responsive">
                                    <table className="table sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Main CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Grow Your Financial Services Business?</h2>
                                <p className="sv-cta-description">
                                    Whether you're a bank, insurance provider, wealth management firm, or professional services company, our comprehensive financial marketing services provide the expertise and compliance awareness you need to grow confidently.
                                </p>
                                <p className="sv-cta-description">
                                    Let's build a compliant marketing strategy that drives growth while maintaining the trust and credibility your clients expect.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="primary">
                                        <i className="fas fa-calendar-check mr-2"></i>
                                        Start Financial Consultation
                                    </Button>
                                    <Button tag={Link} to="/contact" color="light">
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get Compliance Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default FinancialSector;